/* eslint-disable react/prop-types */
import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import moment from 'moment';
import { FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';

const OrderScreenshot = ({ orderId, orderData, userData }) => {
    const captureRef = useRef(null);
    const [isCapturing, setIsCapturing] = useState(false);

    if (!orderData || !orderData.prescriptionItems) return null;

    const items = orderData.prescriptionItems;
    const sortedItemIds = Object.keys(items).sort(
        (a, b) => parseInt(items[a]?.itemId) - parseInt(items[b]?.itemId)
    );

    const formattedTotal = Number(orderData?.total || 0).toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
    });

    const getBatchRows = (item) => {
        if (Array.isArray(item?.invoiceItems) && item.invoiceItems.length > 0) {
            return item.invoiceItems.map((inv) => ({
                batch: inv?.batch || 'NA',
                expiry: inv?.expireDate ? moment(inv.expireDate).format('MM/YY') : 'NA',
                mrp: inv?.mrp != null ? Number(inv.mrp).toFixed(2) : null,
                quantity: inv?.quantity ?? null,
                amount: inv?.amount != null ? Number(inv.amount).toFixed(2) : null,
            }));
        }
        if (Array.isArray(item?.itemBatches) && item.itemBatches.length > 0) {
            return item.itemBatches.map((b) => ({
                batch: b?.batch || 'NA',
                expiry: b?.expireDate ? moment(b.expireDate).format('MM/YY') : 'NA',
                mrp: b?.mrp != null ? Number(b.mrp).toFixed(2) : null,
                quantity: null,
                amount: null,
            }));
        }
        if (item?.itemBatch) {
            return [{
                batch: item.itemBatch?.batch || 'NA',
                expiry: item.itemBatch?.expireDate ? moment(item.itemBatch.expireDate).format('MM/YY') : 'NA',
                mrp: item.itemBatch?.mrp != null ? Number(item.itemBatch.mrp).toFixed(2) : null,
                quantity: null,
                amount: null,
            }];
        }
        return [{ batch: 'NA', expiry: 'NA', mrp: null, quantity: null, amount: null }];
    };

    // Same medicine (itemId) can appear as multiple prescriptionItems entries (duplicates).
    // Group them into one row per unique item, merging their batch rows so no batch info is lost.
    const uniqueItems = [];
    const itemIdToIndex = new Map();
    sortedItemIds.forEach((id) => {
        const item = items[id];
        const itemId = item?.itemId;
        if (itemIdToIndex.has(itemId)) {
            uniqueItems[itemIdToIndex.get(itemId)].batchRows.push(...getBatchRows(item));
        } else {
            itemIdToIndex.set(itemId, uniqueItems.length);
            uniqueItems.push({ item, batchRows: getBatchRows(item) });
        }
    });
    uniqueItems.forEach((entry) => {
        const seen = new Set();
        entry.batchRows = entry.batchRows.filter((row) => {
            const key = `${row.batch}|${row.expiry}|${row.mrp}|${row.quantity}|${row.amount}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    });

    const handleDownload = async () => {
        if (!captureRef.current) return;
        try {
            setIsCapturing(true);
            const node = captureRef.current;
            const previousStyle = {
                left: node.style.left,
                top: node.style.top,
                visibility: node.style.visibility,
            };
            node.style.left = '0px';
            node.style.top = '0px';
            node.style.visibility = 'visible';

            const canvas = await html2canvas(node, {
                scale: 2,
                backgroundColor: '#ffffff',
                useCORS: true,
                logging: false,
            });

            node.style.left = previousStyle.left;
            node.style.top = previousStyle.top;
            node.style.visibility = previousStyle.visibility;

            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = dataUrl;
            const patientName = (userData?.Patient?.name || userData?.User?.name || 'patient')
                .toString()
                .replace(/\s+/g, '_');
            link.download = `order_${orderId}_${patientName}_${moment().format('YYYYMMDD_HHmmss')}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Screenshot downloaded');
        } catch (err) {
            console.error('Failed to capture order screenshot', err);
            toast.error('Failed to download screenshot');
        } finally {
            setIsCapturing(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={handleDownload}
                disabled={isCapturing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white-50 font-semibold shadow hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Download order screenshot"
            >
                <FaCamera />
                {isCapturing ? 'Capturing...' : 'Download Screenshot'}
            </button>

            {/* Off-screen printable layout */}
            <div
                aria-hidden="true"
                style={{
                    position: 'fixed',
                    left: '-99999px',
                    top: '0',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    zIndex: -1,
                }}
            >
                <div
                    ref={captureRef}
                    style={{
                        width: '900px',
                        padding: '24px',
                        backgroundColor: '#ffffff',
                        color: '#111827',
                        fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            borderBottom: '2px solid #069079',
                            paddingBottom: '12px',
                            marginBottom: '16px',
                        }}
                    >
                        <div>
                            <div style={{ fontSize: '22px', fontWeight: 700 }}>
                                Order #{orderId}
                            </div>
                            {userData?.Patient?.name && (
                                <div style={{ fontSize: '14px', color: '#374151', marginTop: '4px' }}>
                                    Patient: <strong>{userData.Patient.name}</strong>
                                </div>
                            )}
                            {userData?.User?.name && (
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                    Customer: {userData.User.name}
                                    {userData?.User?.contact_no ? ` (${userData.User.contact_no})` : ''}
                                </div>
                            )}
                            {orderData?.drName && (
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                    Dr: {orderData.drName}
                                </div>
                            )}
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>
                            <div>Generated: {moment().format('DD MMM YYYY, hh:mm A')}</div>
                        </div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f3f4f6' }}>
                                <th style={thStyle}>#</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>Medicine</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>Batch</th>
                                <th style={thStyle}>Expiry</th>
                                <th style={thStyle}>MRP (₹)</th>
                                <th style={thStyle}>Qty</th>
                                <th style={thStyle}>Amount (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {uniqueItems.map(({ item, batchRows }, idx) => {
                                const rows = batchRows;
                                const rowSpan = rows.length;
                                return rows.map((row, rIdx) => (
                                    <tr
                                        key={`${item?.itemId}-${rIdx}`}
                                        style={{
                                            backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                                            borderBottom: rIdx === rows.length - 1 ? '1px solid #e5e7eb' : 'none',
                                        }}
                                    >
                                        {rIdx === 0 && (
                                            <>
                                                <td rowSpan={rowSpan} style={{ ...tdStyle, textAlign: 'center', verticalAlign: 'top' }}>
                                                    {idx + 1}
                                                </td>
                                                <td rowSpan={rowSpan} style={{ ...tdStyle, verticalAlign: 'top' }}>
                                                    <div style={{ fontWeight: 600 }}>{item?.item?.name || 'NA'}</div>
                                                    {item?.item?.manufacture && (
                                                        <div style={{ color: '#6b7280', fontSize: '11px' }}>{item.item.manufacture}</div>
                                                    )}
                                                </td>
                                            </>
                                        )}
                                        <td style={tdStyle}>{row.batch}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.expiry}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.mrp ?? '-'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.quantity ?? '-'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.amount ?? '-'}</td>
                                    </tr>
                                ));
                            })}
                        </tbody>
                    </table>

                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                        <div
                            style={{
                                backgroundColor: '#069079',
                                color: '#ffffff',
                                padding: '12px 20px',
                                borderRadius: '10px',
                                minWidth: '260px',
                                textAlign: 'right',
                            }}
                        >
                            <div style={{ fontSize: '12px', opacity: 0.85 }}>Order Total</div>
                            <div style={{ fontSize: '22px', fontWeight: 700 }}>{formattedTotal}</div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const thStyle = {
    padding: '8px 10px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#0f766e',
    borderBottom: '1px solid #e5e7eb',
    textAlign: 'center',
};

const tdStyle = {
    padding: '8px 10px',
    fontSize: '12px',
    color: '#1f2937',
};

export default OrderScreenshot;
